import React, { useState, useEffect } from 'react';
import {
  Building,
  Users,
  Briefcase,
  CreditCard,
  Activity,
  Clock,
  Search,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  FileText,
  MessageSquare,
  Download,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { supabase } from '../../lib/supabaseClient';

interface ClientProfile {
  user_id: string;
  company_name: string;
  email: string;
  plan: string | null;
  seats: number | null;
  is_2fa_enabled: boolean | null;
  created_at: string;
  is_deleted: boolean | null;
}

interface PaypalBilling {
  account_owner_id: string;
  plan: string | null;
  status: string | null;
  subscription_id: string | null;
  environment: string | null;
  activated_at: string | null;
}

interface JobPosting {
  id: string;
  customer_id: string;
  job_title: string;
  status: string | null;
  created_at: string;
}

interface LoginRecord {
  user_id: string;
  portal: string | null;
  event_type: string | null;
  created_at: string;
}

interface InterviewRequest {
  id: string;
  created_at: string;
  status: string | null;
}

interface TimelineEvent {
  id: string;
  type: 'account_created' | 'plan_activated' | 'job_posting' | 'login' | 'interview_request';
  description: string;
  timestamp: string;
  meta?: string;
}

interface ClientDetail {
  profile: ClientProfile;
  billing: PaypalBilling | null;
  jobPostings: JobPosting[];
  logins: LoginRecord[];
  interviewRequests: InterviewRequest[];
  totalApplications: number;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PlanBadge({ plan }: { plan: string | null }) {
  if (!plan) return null;
  const isPro = plan.toLowerCase() === 'pro';
  return (
    <span
      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
        isPro ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
      }`}
    >
      {isPro ? 'Pro' : 'Starter'}
    </span>
  );
}

function TwoFABadge({ enabled }: { enabled: boolean | null }) {
  if (enabled) {
    return (
      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        2FA On
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
      2FA Off
    </span>
  );
}

function IntegrityBar({ score }: { score: number }) {
  let color = 'bg-red-500';
  if (score >= 75) color = 'bg-emerald-500';
  else if (score >= 50) color = 'bg-yellow-500';
  else if (score >= 25) color = 'bg-orange-500';

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">Integrity Score</span>
        <span className="text-sm font-bold text-gray-900">{score}/100</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div
          className={`h-3 rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

function TimelineIcon({ type }: { type: TimelineEvent['type'] }) {
  const base = 'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0';
  switch (type) {
    case 'account_created':
      return (
        <div className={`${base} bg-emerald-100`}>
          <CheckCircle className="w-4 h-4 text-emerald-600" />
        </div>
      );
    case 'plan_activated':
      return (
        <div className={`${base} bg-yellow-100`}>
          <CreditCard className="w-4 h-4 text-yellow-600" />
        </div>
      );
    case 'job_posting':
      return (
        <div className={`${base} bg-blue-100`}>
          <Briefcase className="w-4 h-4 text-blue-600" />
        </div>
      );
    case 'login':
      return (
        <div className={`${base} bg-gray-100`}>
          <Activity className="w-4 h-4 text-gray-500" />
        </div>
      );
    case 'interview_request':
      return (
        <div className={`${base} bg-teal-100`}>
          <MessageSquare className="w-4 h-4 text-teal-600" />
        </div>
      );
    default:
      return (
        <div className={`${base} bg-gray-100`}>
          <Clock className="w-4 h-4 text-gray-500" />
        </div>
      );
  }
}

export default function ClientAudit() {
  const [clients, setClients] = useState<ClientProfile[]>([]);
  const [filteredClients, setFilteredClients] = useState<ClientProfile[]>([]);
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientProfile | null>(null);
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Load all clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      const { data, error } = await supabase
        .from('workday_client_profiles')
        .select('user_id, company_name, email, plan, seats, is_2fa_enabled, created_at, is_deleted')
        .order('created_at', { ascending: false });

      if (!error && data) {
        setClients(data as ClientProfile[]);
        setFilteredClients(data as ClientProfile[]);
      }
      setLoadingClients(false);
    };
    fetchClients();
  }, []);

  // Filter clients when search changes
  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setFilteredClients(clients);
      return;
    }
    setFilteredClients(
      clients.filter(
        (c) =>
          (c.company_name || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q)
      )
    );
  }, [search, clients]);

  // Load detail when selected client changes
  useEffect(() => {
    if (!selectedClient) {
      setDetail(null);
      return;
    }
    const fetchDetail = async () => {
      setLoadingDetail(true);
      const userId = selectedClient.user_id;

      const [billingRes, jobsRes, loginsRes, interviewRes] = await Promise.all([
        supabase
          .from('paypal_billing')
          .select('account_owner_id, plan, status, subscription_id, environment, activated_at')
          .eq('account_owner_id', userId)
          .maybeSingle(),
        supabase
          .from('workday_client_job_postings')
          .select('id, customer_id, job_title, status, created_at')
          .eq('customer_id', userId)
          .order('created_at', { ascending: false }),
        supabase
          .from('login_records')
          .select('user_id, portal, event_type, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('interview_requests')
          .select('id, created_at, status')
          .or(`workday_client_id.eq.${userId},customer_id.eq.${userId}`)
          .order('created_at', { ascending: false }),
      ]);

      const jobIds: string[] = (jobsRes.data || []).map((j: any) => j.id);
      let totalApplications = 0;
      if (jobIds.length > 0) {
        const { count } = await supabase
          .from('job_applications')
          .select('*', { count: 'exact', head: true })
          .in('job_id', jobIds);
        totalApplications = count || 0;
      }

      setDetail({
        profile: selectedClient,
        billing: billingRes.data || null,
        jobPostings: jobsRes.data || [],
        logins: loginsRes.data || [],
        interviewRequests: interviewRes.data || [],
        totalApplications,
      });
      setLoadingDetail(false);
    };
    fetchDetail();
  }, [selectedClient]);

  function buildTimeline(d: ClientDetail): TimelineEvent[] {
    const events: TimelineEvent[] = [];

    events.push({
      id: 'account_created',
      type: 'account_created',
      description: 'Account created',
      timestamp: d.profile.created_at,
    });

    if (d.billing?.activated_at) {
      events.push({
        id: 'plan_activated',
        type: 'plan_activated',
        description: `Plan activated (${d.billing.plan || 'unknown'})`,
        timestamp: d.billing.activated_at,
        meta: d.billing.subscription_id || undefined,
      });
    }

    for (const job of d.jobPostings) {
      events.push({
        id: `job_${job.id}`,
        type: 'job_posting',
        description: `Job posted: ${job.job_title}`,
        timestamp: job.created_at,
        meta: job.status || undefined,
      });
    }

    for (const ir of d.interviewRequests) {
      events.push({
        id: `ir_${ir.id}`,
        type: 'interview_request',
        description: 'Interview request submitted',
        timestamp: ir.created_at,
        meta: ir.status || undefined,
      });
    }

    for (const login of d.logins) {
      events.push({
        id: `login_${login.created_at}_${Math.random()}`,
        type: 'login',
        description: `Login${login.event_type ? ` (${login.event_type})` : ''}`,
        timestamp: login.created_at,
        meta: login.portal || undefined,
      });
    }

    events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return events;
  }

  function calcIntegrity(d: ClientDetail): number {
    let score = 0;
    if (d.profile.is_2fa_enabled) score += 25;
    if ((d.profile.plan || '').toLowerCase() === 'pro') score += 25;
    if (d.billing && d.billing.status === 'active') score += 25;
    if (!d.profile.is_deleted) score += 25;
    return score;
  }

  const activeJobs = detail
    ? detail.jobPostings.filter((j) => j.status === 'active' || j.status === 'open').length
    : 0;

  return (
    <AdminLayout>
      <div className="flex flex-col h-full min-h-screen bg-gray-50">
        {/* Page header */}
        <div className="px-6 py-5 border-b border-gray-200 bg-white flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Client Audit</h1>
            <p className="text-sm text-gray-500 mt-1">Per-client audit timelines and integrity scores</p>
          </div>
          {detail && (
            <button
              onClick={() => {
                const events = buildTimeline(detail);
                const rows = events.map((e) => [e.type, e.description, e.timestamp, JSON.stringify(e.meta ?? {})]);
                const csv = [['type', 'description', 'timestamp', 'meta'], ...rows]
                  .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
                  .join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `client-audit-${selectedClient?.user_id ?? 'export'}-${Date.now()}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          )}
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left panel */}
          <div className="w-1/3 border-r border-gray-200 bg-white flex flex-col">
            {/* Search */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by company or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                />
              </div>
            </div>

            {/* Client list */}
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {loadingClients ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-400">No clients found</div>
              ) : (
                filteredClients.map((client) => {
                  const isSelected = selectedClient?.user_id === client.user_id;
                  return (
                    <button
                      key={client.user_id}
                      onClick={() => setSelectedClient(client)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                        isSelected ? 'bg-gray-100' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Building className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {client.company_name || '(No company)'}
                          </span>
                          <PlanBadge plan={client.plan} />
                          <TwoFABadge enabled={client.is_2fa_enabled} />
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{client.email}</p>
                      </div>
                      {isSelected && <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Right panel */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {!selectedClient ? (
              <div className="flex flex-col items-center justify-center h-full py-32 text-center">
                <Building className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-500 text-base font-medium">Select a client to view audit timeline</p>
                <p className="text-gray-400 text-sm mt-1">Choose a client from the left panel</p>
              </div>
            ) : loadingDetail ? (
              <div className="flex items-center justify-center py-32">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              </div>
            ) : detail ? (
              <div className="p-6 space-y-6">
                {/* Client header */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Building className="w-6 h-6 text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl font-bold text-gray-900">
                          {detail.profile.company_name || '(No company)'}
                        </h2>
                        <PlanBadge plan={detail.profile.plan} />
                        {!detail.profile.is_2fa_enabled && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            2FA Not Enabled
                          </span>
                        )}
                        {(detail.profile.plan || '').toLowerCase() === 'starter' && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            Starter Plan
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{detail.profile.email}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-gray-400" />
                          {detail.profile.seats ?? 0} seats
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          Member since {new Date(detail.profile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          {detail.profile.is_2fa_enabled ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-orange-400" />
                          )}
                          2FA {detail.profile.is_2fa_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Metrics row */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <Briefcase className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-gray-900">{activeJobs}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Active Jobs</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <FileText className="w-5 h-5 text-teal-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-gray-900">{detail.totalApplications}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Total Applications</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                    <MessageSquare className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-gray-900">{detail.interviewRequests.length}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Interview Requests</p>
                  </div>
                </div>

                {/* Integrity score */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <IntegrityBar score={calcIntegrity(detail)} />
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500">
                    <span className={`flex items-center gap-1 ${detail.profile.is_2fa_enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> 2FA enabled (+25)
                    </span>
                    <span className={`flex items-center gap-1 ${(detail.profile.plan || '').toLowerCase() === 'pro' ? 'text-emerald-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> Pro plan (+25)
                    </span>
                    <span className={`flex items-center gap-1 ${detail.billing?.status === 'active' ? 'text-emerald-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> Active subscription (+25)
                    </span>
                    <span className={`flex items-center gap-1 ${!detail.profile.is_deleted ? 'text-emerald-600' : 'text-gray-400'}`}>
                      <CheckCircle className="w-3.5 h-3.5" /> Account not deleted (+25)
                    </span>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-gray-500" />
                    Audit Timeline
                  </h3>
                  {buildTimeline(detail).length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">No events recorded</p>
                  ) : (
                    <ol className="relative border-l border-gray-200 ml-4 space-y-6">
                      {buildTimeline(detail).map((event) => (
                        <li key={event.id} className="ml-4">
                          <div className="absolute -left-4">
                            <TimelineIcon type={event.type} />
                          </div>
                          <div className="pl-2">
                            <p className="text-sm font-medium text-gray-900">{event.description}</p>
                            {event.meta && (
                              <p className="text-xs text-gray-500 mt-0.5 capitalize">{event.meta}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5">{formatDate(event.timestamp)}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
